const DEFAULT_CHANNEL_RATES_HZ = {
  playhead: 20,
  cursor: 20,
  selection: 10,
  intent: 5,
  navigation: 12
};

const PRESENCE_TELEMETRY_LOCAL_STORAGE_KEY = 'cookup_debug_presence_telemetry';

const CHANNEL_COUNTER_KEYS = [
  'immediate_sent',
  'delayed_flushed',
  'dropped_redundant',
  'forced_sent'
];

const noop = () => {};

function defaultNow() {
  return Date.now();
}

function normalizeRateHz(rateHz) {
  const parsed = Number(rateHz);
  if (!Number.isFinite(parsed) || parsed <= 0) return 1;
  return parsed;
}

function intervalFromRateHz(rateHz) {
  return Math.floor(1000 / normalizeRateHz(rateHz));
}

function defaultSerialize(payload) {
  try {
    return JSON.stringify(payload);
  } catch (_) {
    return String(payload);
  }
}

function defaultChannelCounters() {
  return {
    immediate_sent: 0,
    delayed_flushed: 0,
    dropped_redundant: 0,
    forced_sent: 0
  };
}

function buildTelemetryShape() {
  const channels = {};
  Object.keys(DEFAULT_CHANNEL_RATES_HZ).forEach((channel) => {
    channels[channel] = defaultChannelCounters();
  });
  return { channels };
}

function readLocalStorageBoolean(key) {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    const value = window.localStorage.getItem(key);
    if (!value) return false;
    const normalized = String(value).toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'on';
  } catch (_) {
    return false;
  }
}

function readEnvBoolean(name) {
  if (typeof process === 'undefined' || !process?.env) return false;
  const value = process.env[name];
  if (!value) return false;
  const normalized = String(value).toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'on';
}

function resolveTelemetryEnabled(explicitEnabled) {
  if (typeof explicitEnabled === 'boolean') return explicitEnabled;
  if (readEnvBoolean('REACT_APP_DEBUG_PRESENCE_TELEMETRY')) return true;
  return readLocalStorageBoolean(PRESENCE_TELEMETRY_LOCAL_STORAGE_KEY);
}

function ensureChannelState(store, channel) {
  if (!store[channel]) {
    store[channel] = {
      lastSentAt: Number.NEGATIVE_INFINITY,
      lastSignature: null,
      timerId: null,
      pending: null
    };
  }
  return store[channel];
}

function ensureTelemetryChannel(telemetry, channel) {
  if (!telemetry.channels[channel]) {
    telemetry.channels[channel] = defaultChannelCounters();
  }
  return telemetry.channels[channel];
}

function computeTotals(channels) {
  const totals = {
    immediate_sent: 0,
    delayed_flushed: 0,
    dropped_redundant: 0,
    forced_sent: 0,
    total_sent: 0,
    total_dropped: 0
  };

  Object.values(channels).forEach((channelCounters) => {
    CHANNEL_COUNTER_KEYS.forEach((key) => {
      totals[key] += Number(channelCounters[key] || 0);
    });
  });

  totals.total_sent = totals.immediate_sent + totals.delayed_flushed + totals.forced_sent;
  totals.total_dropped = totals.dropped_redundant;
  return totals;
}

export function createPresenceRateLimiter(options = {}) {
  const {
    ratesHz = DEFAULT_CHANNEL_RATES_HZ,
    now = defaultNow,
    scheduleTimer = setTimeout,
    clearScheduledTimer = clearTimeout,
    serialize = defaultSerialize,
    telemetryEnabled: telemetryEnabledOverride,
    telemetrySurface
  } = options;

  const stateByChannel = {};
  const telemetryEnabled = resolveTelemetryEnabled(telemetryEnabledOverride);
  const telemetry = telemetryEnabled ? buildTelemetryShape() : null;

  const incrementCounter = (channel, key) => {
    if (!telemetry) return;
    const channelCounters = ensureTelemetryChannel(telemetry, channel);
    channelCounters[key] = Number(channelCounters[key] || 0) + 1;
  };

  const getIntervalMs = (channel) => intervalFromRateHz(ratesHz[channel] || DEFAULT_CHANNEL_RATES_HZ[channel] || 1);

  const clearChannelTimer = (channelState) => {
    if (channelState.timerId) {
      clearScheduledTimer(channelState.timerId);
      channelState.timerId = null;
    }
  };

  const emit = (channel, channelState, sendReason) => {
    if (!channelState.pending) return false;

    const { payload, send, signature } = channelState.pending;
    channelState.pending = null;
    clearChannelTimer(channelState);

    send(payload);
    channelState.lastSentAt = now();
    channelState.lastSignature = signature;

    if (sendReason) {
      incrementCounter(channel, sendReason);
    }

    return true;
  };

  const schedule = (channel, payload, send = noop, scheduleOptions = {}) => {
    const {
      force = false,
      signature = serialize(payload)
    } = scheduleOptions;

    const channelState = ensureChannelState(stateByChannel, channel);

    if (force) {
      channelState.pending = { payload, send, signature };
      return emit(channel, channelState, 'forced_sent');
    }

    const timeNow = now();
    const intervalMs = getIntervalMs(channel);
    const elapsedMs = timeNow - channelState.lastSentAt;

    const duplicateOfPending = channelState.pending?.signature === signature;
    const duplicateOfLast = channelState.lastSignature === signature && elapsedMs < intervalMs;
    if (duplicateOfPending || duplicateOfLast) {
      incrementCounter(channel, 'dropped_redundant');
      return false;
    }

    if (elapsedMs >= intervalMs) {
      channelState.pending = { payload, send, signature };
      return emit(channel, channelState, 'immediate_sent');
    }

    channelState.pending = { payload, send, signature };

    if (!channelState.timerId) {
      channelState.timerId = scheduleTimer(() => {
        emit(channel, channelState, 'delayed_flushed');
      }, intervalMs - elapsedMs);
    }

    return false;
  };

  const reset = (channel) => {
    if (channel) {
      const channelState = ensureChannelState(stateByChannel, channel);
      clearChannelTimer(channelState);
      channelState.pending = null;
      channelState.lastSentAt = Number.NEGATIVE_INFINITY;
      channelState.lastSignature = null;
      return;
    }

    Object.values(stateByChannel).forEach((channelState) => {
      clearChannelTimer(channelState);
      channelState.pending = null;
      channelState.lastSentAt = Number.NEGATIVE_INFINITY;
      channelState.lastSignature = null;
    });
  };

  const resetTelemetry = (channel) => {
    if (!telemetry) return;

    if (channel) {
      telemetry.channels[channel] = defaultChannelCounters();
      return;
    }

    Object.keys(telemetry.channels).forEach((name) => {
      telemetry.channels[name] = defaultChannelCounters();
    });
  };

  const getTelemetrySnapshot = () => {
    if (!telemetry) return null;
    const channels = {};
    Object.entries(telemetry.channels).forEach(([channel, counters]) => {
      channels[channel] = { ...counters };
    });

    return {
      enabled: true,
      channels,
      totals: computeTotals(channels)
    };
  };

  const destroy = () => {
    reset();
  };

  if (telemetryEnabled && typeof telemetrySurface === 'function') {
    telemetrySurface({ getTelemetrySnapshot, resetTelemetry });
  }

  return {
    schedule,
    reset,
    destroy,
    getIntervalMs,
    telemetryEnabled,
    getTelemetrySnapshot,
    resetTelemetry
  };
}

export { DEFAULT_CHANNEL_RATES_HZ, PRESENCE_TELEMETRY_LOCAL_STORAGE_KEY };
